import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { PAYWALL_CONSTANTS } from '@app/modules/paywall/constants/paywall.constants';
import { PaywallLimitService } from '@app/modules/paywall/services/paywall-limit.service';
import { PaywallService } from '@app/modules/paywall/services/paywall.service';

/** How long a coach has to be on Saideline before leaving can open the paywall. */
export const EXIT_INTENT_DELAY_MS = 30_000;

/** Sections where leaving never opens the paywall: the paywall itself, sign-in, legal and admin. */
const EXCLUDED_SECTIONS: string[] = [
    ROUTE_CONSTANTS.UPGRADE,
    ROUTE_CONSTANTS.AUTH.ROOT,
    ROUTE_CONSTANTS.LEGAL.ROOT,
    ROUTE_CONSTANTS.ADMIN.ROOT,
];

/** Profile pages where it never opens: billing (the coach is choosing a plan already) and account deletion. */
const EXCLUDED_PROFILE_PAGES: string[] = [ROUTE_CONSTANTS.USER.BILLING, ROUTE_CONSTANTS.USER.DELETE_ACCOUNT];

/**
 * Exit intent: when a free coach without free sessions left has been on Saideline for 30 seconds and moves
 * the mouse out of the page through the top edge (towards the tab bar or the close button), the paywall
 * opens, once per coach — and not at all in a visit where the paywall already opened, so closing it never
 * brings it back. Only on devices with a mouse; phones and tablets have no such gesture.
 */
@Injectable({ providedIn: 'root' })
export class ExitIntentService {
    private startedAt = 0;
    private isWatching = false;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly router: Router,
        private readonly authService: AuthApiService,
        private readonly zone: NgZone,
        private readonly paywallLimitService: PaywallLimitService,
        private readonly paywallService: PaywallService
    ) {}

    /** Starts watching the mouse. Called once when the app starts. */
    watch(): void {
        const window = this.document.defaultView;
        if (this.isWatching || !window?.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
            return;
        }

        this.isWatching = true;
        this.startedAt = Date.now();

        // Outside Angular: mouse movements must not trigger change detection
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('mouseout', (event) => this.onMouseOut(event));
        });
    }

    private onMouseOut(event: MouseEvent): void {
        // Only leaving the window through the top edge counts, not moving between elements
        if (event.relatedTarget || event.clientY > 0) {
            return;
        }

        const userId = this.authService.currentUser?.sub;
        if (!userId || !this.shouldShow(userId)) {
            return;
        }

        // Counted before it opens: a second mouse-out on the way to the paywall must not open it again
        this.paywallLimitService.markShown(PAYWALL_CONSTANTS.SOURCES.EXIT_INTENT, userId);
        this.zone.run(() => this.paywallService.open(PAYWALL_CONSTANTS.SOURCES.EXIT_INTENT));
    }

    private shouldShow(userId: string): boolean {
        if (
            Date.now() - this.startedAt < EXIT_INTENT_DELAY_MS ||
            !this.paywallService.canOpen ||
            !this.paywallLimitService.canShow(PAYWALL_CONSTANTS.SOURCES.EXIT_INTENT, userId)
        ) {
            return false;
        }

        // Only once the free sessions are used up: before that the paywall never opens by itself
        if (!this.paywallService.isOutOfFreeSessions(this.authService.currentUser)) {
            return false;
        }

        const segments = this.router.parseUrl(this.router.url).root.children['primary']?.segments ?? [];
        const [section = '', page = ''] = segments.map((segment) => segment.path);
        if (section === ROUTE_CONSTANTS.USER.ROOT) {
            return !EXCLUDED_PROFILE_PAGES.includes(page);
        }
        return !EXCLUDED_SECTIONS.includes(section);
    }
}
